/* eslint-disable no-console */
import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import '@nike/eds/dist/index.css';
import './app.css';

import { onReceiveNposMessage } from './components/ElectronicJournal/Npos/nposUtils';
import Home from './components/Home';
import OktaAuth from './components/OktaAuth';
import AuthError from './components/OktaAuth/AuthError';
import Authorize from './components/OktaAuth/Authorize';
import Unauthorized from './components/OktaAuth/Unauthorized';
import PrivateRoute from './components/PrivateRoute';
import RetailAuth from './components/RetailAuth';
import LoadingMessage from './components/RetailAuth/LoadingMessage';
import store from './store';
import Auth, { decodeTokenPayload } from './utils/Auth';
import {
 TRUE_APP, NPOS_APP, WAIT_SECONDS_BEFORE_FAIL
} from './utils/constants';
import { parseNposResponse } from './utils/ej-utils';
import {
  AppName as APP_NAME, AuthTypes, isRunningIframe, getApplicationSource, getTargetOrigin, Messages, isRunningInsideNposApp,
} from './utils/iframeMessaging';
import { postWkMessage, EJ_NPOS_REQUEST } from './utils/webkitMessaging';

const App = () => {
  const targetOrigin = getTargetOrigin();
  const [initialLogin, setInitialLogin] = useState(true);
  const [loadingInsideSourceApp, setLoadingInsideSourceApp] = useState(true);

  const setAccessTokenInfo = (e, ejAppSource) => {
    try {
      const { authType } = e.data;
      const isTruePlatform = ejAppSource === TRUE_APP;
      console.log(`Received data from source app ejAppSource: ${ejAppSource} authType:${authType}`, e?.data);
      // For Retail Athelete login via TRUE & NPOS App
      if (authType === AuthTypes.RetailAuth) {
        const parsedToken = e.data.token.accessToken;
        console.log(`parsedToken:`, parsedToken, authType);
        localStorage.setItem('accessToken', parsedToken);
        const { exp } = decodeTokenPayload(parsedToken);
        localStorage.setItem('expiresAt', exp);
        localStorage.setItem('authSource', authType);
        localStorage.setItem('tokenVersion', Auth.getVersionFromToken());

        setInitialLogin(false);
      }
      if (isTruePlatform) {
        if (e.data.url) {
          localStorage.setItem('true.parentUrl', JSON.stringify(e.data.url));
        }
        // For okta login via TRUE
        if (authType === AuthTypes.OktaAuth) {
          const parsedToken = JSON.parse(e.data.token);
          console.log(`parsedToken:`, parsedToken, authType);

          const accessTokenValue = parsedToken.idToken.idToken;
          localStorage.setItem('accessToken', accessTokenValue);
          const { exp } = decodeTokenPayload(accessTokenValue);
          localStorage.setItem('expiresAt', exp);
          localStorage.setItem('authSource', authType);
          setInitialLogin(false);
        }
      }

      // Load the EJ Page as soon as the Token is recieved from the Source App
      if (Auth.isTokenRequestComplete(ejAppSource, e.data)) setLoadingInsideSourceApp(false);
    } catch (error) {
      console.error(`Error in setAccessTokenInfo for ejAppSource: ${ejAppSource}`, error, e?.data);
      setLoadingInsideSourceApp(false);
    }
  };

  // eslint-disable-next-line fp/no-mutation
  window.setNposEjToken = (token) => {
    try {
      const tokenObj = parseNposResponse(token);
      setAccessTokenInfo(tokenObj, NPOS_APP);
    } catch (ex) {
      /* Token format error while parsing */
    }
  };

  // eslint-disable-next-line fp/no-mutation
  window.onReceiveNposMessage = (message, payload) => onReceiveNposMessage(message, payload, store);

  /** This will initiate communication with the source application. i.e. NPOS App, True Platform * */
  const initCommunicationWithSourceApp = (ejAppSource) => {
    if (![TRUE_APP, NPOS_APP].includes(ejAppSource)) return;
    setLoadingInsideSourceApp(true);
    switch (ejAppSource) {
      case TRUE_APP:
        try {
          console.log(`Sending token request to ejAppSource: ${ejAppSource}, targetOrigin:${targetOrigin}`);
          window.parent.postMessage({ name: Messages.RequestAccessToken }, targetOrigin);
          window.parent.postMessage({ name: Messages.SetTitle, title: APP_NAME }, targetOrigin);
          window.parent.postMessage({ name: Messages.GetURL }, targetOrigin);
          // eslint-disable-next-line fp/no-mutation
          window.onmessage = (e) => {
            setAccessTokenInfo(e, ejAppSource);
          };
        } catch (error) {
          console.log(`error while Sending token request to ejAppSource: ${ejAppSource}, targetOrigin:${targetOrigin}`, error);
          Auth.sourceAppLogout();
        }
        break;
      case NPOS_APP:
        // eslint-disable-next-line no-unused-expressions
        try {
          postWkMessage(EJ_NPOS_REQUEST.TOKEN);
        } catch (ex) {
          Auth.sourceAppLogout();
        }
        break;
      default: break;
    }
  };

  const setTokens = () => {
    /* If the Source App doesn't respond with Token within WAIT_SECONDS_BEFORE_FAIL
    app will show error & user needs to relaunch EJ */
    setTimeout(() => {
      setLoadingInsideSourceApp(false);
    }, WAIT_SECONDS_BEFORE_FAIL);
    const ejAppSource = getApplicationSource();
    initCommunicationWithSourceApp(ejAppSource);
  };
  useEffect(() => {
    if (isRunningIframe() || isRunningInsideNposApp()) {
      setTokens();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if ((isRunningIframe() || isRunningInsideNposApp()) && !initialLogin && loadingInsideSourceApp) {
      setTokens();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingInsideSourceApp]);
  const changeLoading = () => { setLoadingInsideSourceApp(true); };
  if (loadingInsideSourceApp && (isRunningIframe() || isRunningInsideNposApp())) {
    return (<LoadingMessage />);
  } else {
    return (
      <Router>
        <Provider store={store}>
          <Switch>
            <Route exact component={OktaAuth} path="/login" />
            <Route exact component={RetailAuth} path="/loginretail" />
            <Route exact component={Authorize} path="/auth" />
            <Route exact component={AuthError} path="/error" />
            <Route exact component={Unauthorized} path="/unauthorized" />
            <PrivateRoute exact path="/">
              <Home changeLoading={changeLoading} />
            </PrivateRoute>
          </Switch>
        </Provider>
      </Router>
    );
  }
};
export default App;
